import cx from 'classnames'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { canShare } from '../../client/canShare'
import { useHeader, usePage, useWindow } from '../../contexts'
import { he } from '../../locales/he'
import { colors, maxWidth, maxWidthFl } from '../../styles/GlobalVariables'
import {
  IBigBrotherButton,
  IHeader,
  IHeaderMenu,
  IOpenGraph,
} from '../../types'
import { mediaCdnReplace, urlsEqual } from '../../utils'
import Link from '../Link/Link'
import { LogoComp } from '../Logo'
import { mediaCdn, utils } from '../Player/Helpers'
import {
  Action,
  BBLiveIcon,
  ButtonsWrapper,
  Children,
  Container,
  DesktopMenu,
  Group,
  HamburgerIcon,
  HeaderContainer,
  HeaderPlaceHolder,
  Item,
  ItemsWrapper,
  LiveIcon,
  MenuArrow,
  MenuBack,
  MenuChild,
  MobileMenu,
  SearchIcon,
  ShareWrapper,
} from './Header.styles'
import { SubMenu } from './SubMenu'
import MenuArrowMobileIcon from '/public/assets/images/svg/menu-arrow-mobile.svg'
import MenuBackIcon from '/public/assets/images/svg/menu-back.svg'
import MenuCloseIcon from '/public/assets/images/svg/menu-close.svg'
import MenuOpenIcon from '/public/assets/images/svg/menu-open.svg'
import MenuSearchLightIcon from '/public/assets/images/svg/menu-search-light.svg'
import { MenuArrowDesktopIcon, ShareIcon } from '../../icons'
import { VodButtonComp } from './VodButton'

type Props = {
  header: IHeader
  className?: string
  pageType: string
  ogData: IOpenGraph
  displaySubmenu: boolean
  bbButton?: IBigBrotherButton
}

export const Header = ({
  header,
  pageType,
  className,
  ogData,
  displaySubmenu,
  bbButton,
}: Props) => {
  const { asPath } = useRouter()
  const { setHeaderHeight } = useHeader()
  const headerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const { title } = ogData
  const win = useWindow()
  const page = usePage()
  const initialMenuState = useMemo(() => {
    return {
      showingChildren: false,
      menuItems: header?.mainMenu,
      title: undefined,
    }
  }, [header?.mainMenu])

  const [opened, setOpened] = useState(false)
  const [menuState, setMenuState] = useState<{
    showingChildren: boolean
    menuItems: IHeaderMenu[]
    title?: string
  }>(initialMenuState)

  const handleMenuIconClick = useCallback(() => {
    setOpened(!opened)
    setMenuState(initialMenuState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const handleMobileMenuClick = useCallback(
    (children: IHeaderMenu[], title?: string) => {
      setMenuState({
        showingChildren: !menuState.showingChildren,
        menuItems: children,
        title,
      })
    },
    [menuState]
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !opened ||
        !mobileMenuRef.current ||
        mobileMenuRef.current.contains(event.target as Node)
      ) {
        return
      }
      setOpened(false)
    }

    const handleScroll = () => {
      setIsScrolled(utils.isScrolled())
    }

    document.addEventListener('mousedown', handleClickOutside)

    // TODO: Only do this on some pagetypes
    window.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)

      window.removeEventListener('scroll', handleScroll)
    }
  }, [mobileMenuRef, opened, pageType])

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight)
    }
  }, [headerRef, setHeaderHeight])

  const showShareIcon = useMemo(() => pageType !== 'hp', [pageType])
  const showBBLive = useMemo(
    () => bbButton?.link && bbButton.icon,
    [bbButton?.icon, bbButton?.link]
  ) //&& pageType === 'hp'

  const { showingChildren, menuItems } = menuState

  const activeHeaderGroup = findActiveGroup(
    header?.mainMenu?.slice(0, 7),
    asPath
  )

  return (
    <>
      <HeaderPlaceHolder hasSubMenu={displaySubmenu} />
      <Container
        className={cx(className, isScrolled && 'is-scrolled')}
        isFaceLift={page?.PageMeta.isFaceLift}
        id={'header'}
        displaySubmenu={displaySubmenu}
        ref={headerRef}
      >
        <HeaderContainer
          maxWidth={page?.PageMeta.isFaceLift ? maxWidthFl : maxWidth}
          displaySubmenu={displaySubmenu}
          theme={{ haseWarComp: page?.PageMeta.war_comp?.showWarRail }}
        >
          <HamburgerIcon onClick={handleMenuIconClick}>
            {opened ? <MenuCloseIcon /> : <MenuOpenIcon />}
          </HamburgerIcon>
          <LogoComp />
          <DesktopMenu>
            <ItemsWrapper ref={menuRef}>
              {header?.mainMenu
                ?.slice(0, 7)
                ?.map(({ title, url, target, children }, index) => (
                  <Group
                    key={`menuItem-${index}`}
                    subItems={children.length > 0}
                  >
                    <Item iconSize={12} active={index === activeHeaderGroup}>
                      <Link href={url} target={target}>
                        {title}
                      </Link>
                      {children.length > 0 && <MenuArrowDesktopIcon />}
                    </Item>
                    {children.length > 0 && (
                      <Children
                        background={
                          page?.PageMeta.isFaceLift
                            ? colors.midnightBlue
                            : colors.headerBlue
                        }
                      >
                        {children.map(({ title, url, target }, index) => (
                          <MenuChild
                            href={url}
                            key={`desktopStyledLinkMenuChild-${index}`}
                            target={target}
                          >
                            <div>{title}</div>
                          </MenuChild>
                        ))}
                      </Children>
                    )}
                  </Group>
                ))}
            </ItemsWrapper>
          </DesktopMenu>
          <ButtonsWrapper>
            <Link href='/allshows/screen/search/1196697/' target='_self'>
              <SearchIcon key={'heydaySearch'} id='vodSearch'>
                <MenuSearchLightIcon />
              </SearchIcon>
            </Link>
            <VodButtonComp />
            {showBBLive && (
              <BBLiveIcon href={bbButton?.link || '/bb-livestream/'}>
                <img
                  className='mobile'
                  src={mediaCdnReplace(
                    bbButton?.icon.mobile ||
                      `${mediaCdn}v1654619384/bigBrother/2022/eye_Live_Mobile.webp`
                  )}
                  alt='האח הגדול לייב'
                />
                <img
                  className='desktop'
                  src={mediaCdnReplace(
                    bbButton?.icon.desktop ||
                      `${mediaCdn}v1654615533/bigBrother/2022/eye_Live.webp`
                  )}
                  alt='האח הגדול לייב'
                />
              </BBLiveIcon>
            )}
            <LiveIcon href={'/allshows/2010263/'}>
              <div>LIVE</div>
            </LiveIcon>
            {showShareIcon && win && canShare() && (
              <ShareWrapper
                onClick={() => {
                  navigator.share({
                    title: title || '',
                    url: win.location.href,
                  })
                }}
              >
                <ShareIcon />
              </ShareWrapper>
            )}
          </ButtonsWrapper>
          {displaySubmenu && (
            <SubMenu
              subMenu={header.subMenu}
              isFaceLift={page?.PageMeta.isFaceLift}
            />
          )}
        </HeaderContainer>
        {/*{!page?.PageMeta.isFaceLift && (*/}
        {/*  <LogoWrapper>*/}
        {/*    <TransparentLogo src='https://media3.reshet.tv/image/upload/v1735717783/logo-header-transparent_qbjalq.webp' />*/}
        {/*  </LogoWrapper>*/}
        {/*)}*/}
      </Container>
      <MobileMenu className={className} opened={opened} ref={mobileMenuRef}>
        {showingChildren && (
          <Action>
            <MenuBack
              onClick={() => {
                handleMobileMenuClick(header.mainMenu)
              }}
            >
              <MenuBackIcon />
              {menuState.title || he.sections}
            </MenuBack>
          </Action>
        )}
        {menuItems?.map(({ title, url, target, children }, index) => (
          <Item
            key={`menuItem-${index}`}
            subItems={children.length > 0}
            onClick={() => {
              children.length > 0
                ? handleMobileMenuClick(children, title)
                : setOpened(false)
            }}
          >
            <Link href={url} target={target}>
              {title}
            </Link>
            {children.length > 0 && (
              <MenuArrow>
                <MenuArrowMobileIcon />
              </MenuArrow>
            )}
          </Item>
        ))}
      </MobileMenu>
    </>
  )
}

const findActiveGroup = (menuItems: IHeaderMenu[], asPath: string) => {
  const activeGroup = menuItems?.findIndex(({ url, children }) => {
    if (!children.length || !url) {
      return
    }
    return (
      urlsEqual(url, asPath) ||
      children.some(({ url }) => urlsEqual(url, asPath))
    )
  })
  return activeGroup !== -1 ? activeGroup : null
}
